import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { TillService } from '../../../api/till.service';
import { formatLabel } from '../../../utils/formatLabel';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const session_id = searchParams.get('session_id');

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        setLoading(true);
        if (session_id) {
          const tillService = new TillService();
          const res = await tillService.verifyPayment(session_id);

          if (res.data.success) {
            setPaymentDetails(res.data);
          } else {
            setError('Payment verification failed');
          }
        }

        setLoading(false);
      } catch (err) {
        console.error(err);
        setError('Failed to verify payment details');
        setLoading(false);
      }
    };

    if (session_id) {
      fetchPaymentDetails();
    } else {
      setLoading(false);
    }
  }, [session_id]);

  const handleGoBackToList = () => {
    navigate('/till/list');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <CircularProgress size={60} color="success" />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Verifying your payment...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3,
        bgcolor: '#f5f5f5',
      }}
    >
      <Card
        sx={{
          maxWidth: 500,
          width: '100%',
          textAlign: 'center',
          boxShadow: 3,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          ) : (
            <>
              <CheckCircleIcon
                sx={{
                  fontSize: 80,
                  color: 'success.main',
                  mb: 2,
                }}
              />
              <Typography variant="h4" gutterBottom color="success.main">
                Payment Successful!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Thank you for your payment. Your transaction has been completed
                successfully.
              </Typography>

              {session_id && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Session ID: {session_id}
                </Typography>
              )}

              {paymentDetails && (
                <Box sx={{ mb: 3, textAlign: 'left' }}>
                  <Typography variant="h6" gutterBottom>
                    Payment Details:
                  </Typography>
                  {/* Add more payment details here based on your API response */}
                  <Typography variant="body2">
                    Amount: £{paymentDetails.amount}
                  </Typography>
                  <Typography variant="body2">
                    Payment method: {formatLabel(paymentDetails.paymentMethod)}
                  </Typography>
                </Box>
              )}
            </>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGoBackToList}
              sx={{ minWidth: 120 }}
            >
              Go Back to List
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleGoHome}
              sx={{ minWidth: 120 }}
            >
              Go to Dashboard
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
